import * as React from 'react';
import { Button } from '@vechaiui/react';
import { Form, FormikProps } from 'formik';
import { useLocalization } from '@fluent/react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';

import AddUserForm from '../forms/AddUserForm';
import Formik from '#/components/forms/Formik';
import yup from '#/libs/yup';
import { AdminRoute } from '#/constants/page-route';
import { ApiErrorBlock, ErrorBlockFormik } from '../forms/ErrorBlock';
import { CREATE_USER_INITIAL_VALUES } from '#/constants/form-initial-values';
import {
  CreateUserInput,
  AssignUserCustomerAccessDocument,
  AssignUserLogisticsAccessDocument,
  CreateUserDocument,
  GetUserByIdDocument,
  User,
} from '#/__generated__/graphql/graphql';
import { MANAGE_CUSTOMER, MANAGE_LOGISTIC } from '#/constants/manage-user-type-list';
import { createUserSchema } from '#/constants/validation-schema';
import { sanitizeCreateUserFormData } from '#/utils/sanitize-data';

const AddUserPanel: React.FC<AddUserPanelProps> = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const formRef = React.useRef<FormikProps<any>>(null);
  const [initialValues, setInitialValues] = React.useState(CREATE_USER_INITIAL_VALUES);

  const [createUserMutation] = useMutation(CreateUserDocument);
  const [assignUserCustomerAccess] = useMutation(AssignUserCustomerAccessDocument);
  const [assignUserLogisticsAccessMutation] = useMutation(AssignUserLogisticsAccessDocument);

  const { userId } = useParams();
  const { loading, error, data } = useQuery(GetUserByIdDocument, {
    variables: { id: userId || '' },
    skip: !userId,
  });

  const [apiErrors, setApiErrors] = React.useState<{ message: string }[]>();

  React.useEffect(() => {
    if (!!data?.node) {
      const user = data.node as User;
      const isManagingCustomer = user.manageCustomers.length > 0;
      setInitialValues({
        ...initialValues,
        customerId: user.manageCustomers[0]?.id,
        email: user.email,
        logisticsId: user.manageLogistics[0]?.id,
        manageUserCode: isManagingCustomer ? MANAGE_CUSTOMER.code : MANAGE_LOGISTIC.code,
        username: user.username,
      });
    }
  }, [data?.node]);

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={initialValues}
      onSubmit={async (formData, { resetForm }) => {
        try {
          const createUserInputFormData: CreateUserInput = sanitizeCreateUserFormData(formData);
          await createUserMutation({
            variables: { input: createUserInputFormData },
          }).then(async (res) => {
            const userId = res.data?.createUser?.node.id;
            if (!userId) {
              throw new Error('No User ID');
            }

            if (formData.manageUserCode === MANAGE_LOGISTIC.code) {
              await assignUserLogisticsAccessMutation({
                variables: {
                  input: {
                    logisticsId: formData.logisticsId,
                    userId,
                  },
                },
              });
            } else {
              await assignUserCustomerAccess({
                variables: {
                  input: {
                    customerId: formData.customerId,
                    userId,
                  },
                },
              });
            }
            alert(`User [${formData.username}] created successfully.`);
            resetForm();
            navigate(AdminRoute.UserList);
          });
        } catch (err: any) {
          if (err.errors) {
            setApiErrors(err.errors);
          } else if (err.networkError?.result.errors) {
            setApiErrors(err.networkError.result.errors);
          } else {
            setApiErrors([err]);
          }
        }
      }}
      validationSchema={yup.object(createUserSchema)}
    >
      {(formikProps) => (
        <>
          <Form>
            <AddUserForm readOnly={!!userId} />
            <footer className="mt-12">
              <div>
                {!formikProps.isValid && formikProps.submitCount > 0 && <ErrorBlockFormik />}
                {apiErrors && <ApiErrorBlock errors={apiErrors} />}

                <div className="flex mt-12 space-x-10">
                  <Button
                    className="flex-1"
                    size="lg"
                    type="button"
                    variant="solid"
                    onClick={() => {
                      navigate(AdminRoute.UserList);
                    }}
                  >
                    {!userId ? l10n.getString('cancel') : l10n.getString('back')}
                  </Button>
                  {!userId && (
                    <Button
                      className="flex-1"
                      color="primary"
                      size="lg"
                      type="submit"
                      variant="solid"
                    >
                      {l10n.getString('add')}
                    </Button>
                  )}
                </div>
              </div>
            </footer>
          </Form>
        </>
      )}
    </Formik>
  );
};
export type AddUserPanelProps = {};

export default AddUserPanel;
